import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { TypeIcon, HeadingIcon, MousePointerIcon, ImageIcon, PlayIcon, MinusIcon, MoveVerticalIcon, ColumnsIcon, Share2Icon, Trash2Icon, CopyIcon, GripVerticalIcon, UndoIcon, RedoIcon, PaletteIcon, ChevronRightIcon, TagIcon, XIcon, WandSparklesIcon, Loader2Icon, SendIcon, PlusIcon, ChevronDownIcon } from 'lucide-react';
import { EmailBlock, EmailBlockType, EmailGlobalStyles, DEFAULT_EMAIL_STYLES, EMAIL_PALETTE, createEmailBlock, generateId } from './types';
import { generateEmailHtml } from './emailHtmlGenerator';
import { callGPTAPI } from '../../utils/api';
const ICONS: Record<string, React.ElementType> = {
  Type: TypeIcon,
  Heading: HeadingIcon,
  MousePointer: MousePointerIcon,
  Image: ImageIcon,
  Play: PlayIcon,
  Minus: MinusIcon,
  MoveVertical: MoveVerticalIcon,
  Columns: ColumnsIcon,
  Share2: Share2Icon
};
const SOCIAL_PLATFORMS = ['facebook', 'twitter', 'instagram', 'linkedin', 'youtube', 'tiktok', 'pinterest', 'whatsapp'];
const EMAIL_BEAUTIFY_INSTRUCTIONS = `You are an expert email HTML developer and designer. You receive an email's complete HTML. The user will give you a specific instruction about what to change.

RULES:
1. Return ONLY an HTML code block with the complete email HTML.
2. Keep ALL merge variables like {{confirmation_link}}, {{team_name}}, {{first_name}} etc.
3. Keep the TABLE-BASED layout for email client compatibility.
4. Use inline styles (email clients strip style blocks).
5. CRITICAL: You must ONLY change the specific things the user asked for. Leave ALL other styles, content, and structure exactly as they are.
6. If the user says "make the button green", ONLY change the button color. Do NOT touch fonts, backgrounds, spacing, or anything else.
7. If the user says "change the heading font size", ONLY change that. Do NOT redesign the entire email.
8. Start with the EXACT current HTML and make MINIMAL targeted changes based on the user's instruction.
9. Keep it responsive with the existing @media queries.
10. Do NOT add any explanation — ONLY the html code block:
\`\`\`html
(your modified email HTML here)
\`\`\`
11. If no specific instruction is given, make subtle improvements while keeping the overall look intact — do NOT redesign from scratch.`;
function parseHtmlFromResponse(response: string): string {
  const htmlMatch = response.match(/```html\s*\n([\s\S]*?)```/i);
  if (htmlMatch) return htmlMatch[1].trim();
  const genericMatch = response.match(/```\s*\n([\s\S]*?)```/i);
  if (genericMatch) return genericMatch[1].trim();
  return response.trim();
}
// Types that can be placed inside columns
const COLUMN_ALLOWED_TYPES: EmailBlockType[] = ['text', 'heading', 'button', 'image', 'divider', 'spacer'];
interface EmailBuilderProps {
  initialBlocks?: EmailBlock[];
  initialStyles?: EmailGlobalStyles;
  onSchemaChange: (blocks: EmailBlock[], styles: EmailGlobalStyles) => void;
  onHtmlChange: (html: string) => void;
  mergeVariables?: string[];
}
export function EmailBuilder({
  initialBlocks,
  initialStyles,
  onSchemaChange,
  onHtmlChange,
  mergeVariables = []
}: EmailBuilderProps) {
  const [blocks, setBlocks] = useState<EmailBlock[]>(initialBlocks || []);
  const [styles, setStyles] = useState<EmailGlobalStyles>(initialStyles || DEFAULT_EMAIL_STYLES);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Track selected child block inside a column: { parentId, colIndex, childId }
  const [selectedChild, setSelectedChild] = useState<{
    parentId: string;
    colIndex: number;
    childId: string;
  } | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  // Track drag over column: { parentId, colIndex, position }
  const [dragOverCol, setDragOverCol] = useState<{
    parentId: string;
    colIndex: number;
    position: number;
  } | null>(null);
  const [history, setHistory] = useState<EmailBlock[][]>([initialBlocks || []]);
  const [historyIdx, setHistoryIdx] = useState(0);
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set());
  const [isBeautifying, setIsBeautifying] = useState(false);
  const [beautifyPrompt, setBeautifyPrompt] = useState('');
  const [showBeautifyInput, setShowBeautifyInput] = useState(false);
  // Track HTML before beautify so undo can revert it
  const [preBeautifyHtml, setPreBeautifyHtml] = useState<string | null>(null);
  const dragItemRef = useRef<{
    type: 'palette' | 'canvas';
    blockType?: EmailBlockType;
    index?: number;
  } | null>(null);
  // Find the selected block — could be top-level or a child inside a column
  const selectedBlock = (() => {
    if (selectedChild) {
      const parent = blocks.find(b => b.id === selectedChild.parentId);
      if (parent?.columns?.[selectedChild.colIndex]) {
        return parent.columns[selectedChild.colIndex].find(cb => cb.id === selectedChild.childId) || null;
      }
      return null;
    }
    return blocks.find(b => b.id === selectedId) || null;
  })();
  const pushHistory = useCallback((newBlocks: EmailBlock[]) => {
    setHistory(prev => [...prev.slice(0, historyIdx + 1), newBlocks].slice(-50));
    setHistoryIdx(prev => Math.min(prev + 1, 49));
  }, [historyIdx]);
  const updateBlocks = useCallback((newBlocks: EmailBlock[]) => {
    setBlocks(newBlocks);
    pushHistory(newBlocks);
    const html = generateEmailHtml(newBlocks, styles);
    onSchemaChange(newBlocks, styles);
    onHtmlChange(html);
  }, [styles, pushHistory, onSchemaChange, onHtmlChange]);
  useEffect(() => {
    const html = generateEmailHtml(blocks, styles);
    onSchemaChange(blocks, styles);
    onHtmlChange(html);
  }, [styles]);
  const undo = useCallback(() => {
    // If we have a beautify HTML to revert, do that first
    if (preBeautifyHtml !== null) {
      onHtmlChange(preBeautifyHtml);
      setPreBeautifyHtml(null);
      return;
    }
    if (historyIdx > 0) {
      const newIdx = historyIdx - 1;
      setHistoryIdx(newIdx);
      const prev = history[newIdx];
      setBlocks(prev);
      onSchemaChange(prev, styles);
      onHtmlChange(generateEmailHtml(prev, styles));
    }
  }, [historyIdx, history, styles, onSchemaChange, onHtmlChange, preBeautifyHtml]);
  const redo = useCallback(() => {
    if (historyIdx < history.length - 1) {
      const newIdx = historyIdx + 1;
      setHistoryIdx(newIdx);
      const next = history[newIdx];
      setBlocks(next);
      onSchemaChange(next, styles);
      onHtmlChange(generateEmailHtml(next, styles));
    }
  }, [historyIdx, history, styles, onSchemaChange, onHtmlChange]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        e.shiftKey ? redo() : undo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);
  const handlePaletteDragStart = (type: EmailBlockType) => (e: React.DragEvent) => {
    dragItemRef.current = {
      type: 'palette',
      blockType: type
    };
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', type); // needed for column drops
  };
  const handleCanvasDragStart = (index: number) => (e: React.DragEvent) => {
    dragItemRef.current = {
      type: 'canvas',
      index
    };
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(index);
    setDragOverCol(null);
  };
  const handleBlockDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const dropIdx = e.clientY < midY ? index : index + 1;
    e.dataTransfer.dropEffect = dragItemRef.current?.type === 'palette' ? 'copy' : 'move';
    setDragOverIdx(dropIdx);
    setDragOverCol(null);
  };
  const handleBlockDrop = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const dropIdx = e.clientY < midY ? index : index + 1;
    setDragOverIdx(null);
    setDragOverCol(null);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createEmailBlock(drag.blockType);
      const newBlocks = [...blocks];
      newBlocks.splice(dropIdx, 0, newBlock);
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      if (drag.index === index) {
        dragItemRef.current = null;
        return;
      }
      const newBlocks = [...blocks];
      const [moved] = newBlocks.splice(drag.index, 1);
      const adjustedIdx = drag.index < dropIdx ? dropIdx - 1 : dropIdx;
      newBlocks.splice(adjustedIdx, 0, moved);
      updateBlocks(newBlocks);
    }
    dragItemRef.current = null;
  };
  const handleDrop = (dropIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(null);
    setDragOverCol(null);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createEmailBlock(drag.blockType);
      const newBlocks = [...blocks];
      newBlocks.splice(dropIndex, 0, newBlock);
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      const newBlocks = [...blocks];
      const [moved] = newBlocks.splice(drag.index, 1);
      newBlocks.splice(drag.index < dropIndex ? dropIndex - 1 : dropIndex, 0, moved);
      updateBlocks(newBlocks);
    }
    dragItemRef.current = null;
  };
  const handleCanvasDropEnd = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(null);
    setDragOverCol(null);
    const drag = dragItemRef.current;
    if (drag?.type === 'palette' && drag.blockType) {
      const newBlock = createEmailBlock(drag.blockType);
      updateBlocks([...blocks, newBlock]);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
    }
    dragItemRef.current = null;
  };
  // Column drop handlers
  const handleColumnDragOver = (parentId: string, colIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const drag = dragItemRef.current;
    if (!drag) return;
    // Only allow palette drops of allowed types into columns
    if (drag.type === 'palette' && drag.blockType && COLUMN_ALLOWED_TYPES.includes(drag.blockType)) {
      const parent = blocks.find(b => b.id === parentId);
      const col = parent?.columns?.[colIndex] || [];
      setDragOverCol({
        parentId,
        colIndex,
        position: col.length
      });
      setDragOverIdx(null);
      e.dataTransfer.dropEffect = 'copy';
    }
  };
  const handleColumnDrop = (parentId: string, colIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCol(null);
    setDragOverIdx(null);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType && COLUMN_ALLOWED_TYPES.includes(drag.blockType)) {
      const newBlock = createEmailBlock(drag.blockType);
      const newBlocks = blocks.map(b => {
        if (b.id !== parentId) return b;
        const cols = (b.columns || []).map((col, i) => i === colIndex ? [...col, newBlock] : col);
        return {
          ...b,
          columns: cols
        };
      });
      updateBlocks(newBlocks);
      setSelectedChild({
        parentId,
        colIndex,
        childId: newBlock.id
      });
      setSelectedId(null);
    }
    dragItemRef.current = null;
  };
  const deleteBlock = (id: string) => {
    updateBlocks(blocks.filter(b => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  };
  const deleteChildBlock = (parentId: string, colIndex: number, childId: string) => {
    const newBlocks = blocks.map(b => {
      if (b.id !== parentId) return b;
      const cols = (b.columns || []).map((col, i) => i === colIndex ? col.filter(cb => cb.id !== childId) : col);
      return {
        ...b,
        columns: cols
      };
    });
    updateBlocks(newBlocks);
    if (selectedChild?.childId === childId) setSelectedChild(null);
  };
  const duplicateBlock = (id: string) => {
    const idx = blocks.findIndex(b => b.id === id);
    if (idx === -1) return;
    const original = blocks[idx];
    const clone: EmailBlock = {
      ...original,
      id: generateId(),
      columns: original.columns ? original.columns.map(col => col.map(cb => ({
        ...cb,
        id: generateId()
      }))) : undefined
    };
    const newBlocks = [...blocks];
    newBlocks.splice(idx + 1, 0, clone);
    updateBlocks(newBlocks);
    setSelectedId(clone.id);
    setSelectedChild(null);
  };
  const updateBlock = (id: string, updates: Partial<EmailBlock>) => {
    const newBlocks = blocks.map(b => b.id === id ? {
      ...b,
      ...updates
    } : b);
    setBlocks(newBlocks);
    onSchemaChange(newBlocks, styles);
    onHtmlChange(generateEmailHtml(newBlocks, styles));
  };
  const updateChildBlock = (parentId: string, colIndex: number, childId: string, updates: Partial<EmailBlock>) => {
    const newBlocks = blocks.map(b => {
      if (b.id !== parentId) return b;
      const cols = (b.columns || []).map((col, i) => i === colIndex ? col.map(cb => cb.id === childId ? {
        ...cb,
        ...updates
      } : cb) : col);
      return {
        ...b,
        columns: cols
      };
    });
    setBlocks(newBlocks);
    onSchemaChange(newBlocks, styles);
    onHtmlChange(generateEmailHtml(newBlocks, styles));
  };
  const commitToHistory = () => pushHistory(blocks);
  const categories = [...new Set(EMAIL_PALETTE.map(p => p.category))];
  const toggleCat = (cat: string) => {
    setCollapsedCats(prev => {
      const n = new Set(prev);
      n.has(cat) ? n.delete(cat) : n.add(cat);
      return n;
    });
  };
  // AI Beautify handler
  const handleBeautify = async (styleDirection?: string) => {
    if (blocks.length === 0) return;
    setIsBeautifying(true);
    try {
      const html = generateEmailHtml(blocks, styles);
      // Save current HTML so undo can revert
      setPreBeautifyHtml(html);
      const prompt = `Here is my email HTML:\n\`\`\`html\n${html}\n\`\`\`\n\n${styleDirection ? `User instruction: ${styleDirection}` : 'Make subtle improvements to polish the email. Keep the overall look intact.'}`;
      const result = await callGPTAPI(EMAIL_BEAUTIFY_INSTRUCTIONS, prompt);
      const newHtml = parseHtmlFromResponse(result);
      if (newHtml) onHtmlChange(newHtml);
    } catch (err) {
      console.error('Email beautify failed:', err);
      setPreBeautifyHtml(null); // Clear on failure
    } finally {
      setIsBeautifying(false);
      setShowBeautifyInput(false);
      setBeautifyPrompt('');
    }
  };
  // Handle property changes — route to correct updater based on selection
  const handlePropertyChange = (updates: Partial<EmailBlock>) => {
    if (selectedChild) {
      updateChildBlock(selectedChild.parentId, selectedChild.colIndex, selectedChild.childId, updates);
    } else if (selectedId) {
      updateBlock(selectedId, updates);
    }
  };
  const handlePropertyDelete = () => {
    if (selectedChild) {
      deleteChildBlock(selectedChild.parentId, selectedChild.colIndex, selectedChild.childId);
    } else if (selectedId) {
      deleteBlock(selectedId);
    }
  };
  return <div className="flex h-[calc(100vh-220px)] min-h-[500px] border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-134">
      {/* Left — Palette */}
      <div className="w-56 flex-shrink-0 border-r border-gray-200 bg-gray-50 overflow-y-auto" data-id="element-135">
        <div className="p-3 border-b border-gray-200" data-id="element-136">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-137">
            Email Blocks
          </h3>
        </div>
        {categories.map(cat => <div key={cat} data-id="element-138">
            <button onClick={() => toggleCat(cat)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-100" data-id="element-139">
              {cat}
              <ChevronRightIcon className={`h-3 w-3 transition-transform ${collapsedCats.has(cat) ? '' : 'rotate-90'}`} data-id="element-140" />
            </button>
            {!collapsedCats.has(cat) && <div className="px-2 pb-2 space-y-1" data-id="element-141">
                {EMAIL_PALETTE.filter(p => p.category === cat).map(item => {
            const Icon = ICONS[item.icon] || TypeIcon;
            return <div key={item.type} draggable onDragStart={handlePaletteDragStart(item.type as EmailBlockType)} className="flex items-center gap-2 px-2.5 py-2 rounded-md bg-white border border-gray-200 text-sm text-gray-700 cursor-grab hover:border-blue-300 hover:shadow-sm active:cursor-grabbing select-none" data-id="element-142">
                      <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-143" />
                      <span className="truncate" data-id="element-144">{item.label}</span>
                    </div>;
          })}
              </div>}
          </div>)}

        {mergeVariables.length > 0 && <div className="border-t border-gray-200 mt-2 pt-2 px-3 pb-3" data-id="element-145">
            <div className="flex items-center gap-1 mb-2" data-id="element-146">
              <TagIcon className="h-3 w-3 text-gray-400" data-id="element-147" />
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-148">
                Merge Tags
              </span>
            </div>
            <div className="flex flex-wrap gap-1" data-id="element-149">
              {mergeVariables.map(v => <button key={v} onClick={() => navigator.clipboard?.writeText(`{{${v}}}`)} className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100" title={`Copy {{${v}}}`} data-id="element-150">
                  {`{{${v}}}`}
                </button>)}
            </div>
          </div>}
      </div>

      {/* Center — Canvas */}
      <div className="flex-1 overflow-y-auto bg-gray-100 p-6" onClick={() => {
      setSelectedId(null);
      setSelectedChild(null);
    }} data-id="element-151">
        <div className="flex items-center justify-between mb-4" data-id="element-152">
          <div className="flex items-center gap-1" data-id="element-153">
            <button onClick={undo} disabled={historyIdx <= 0} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" title="Undo" data-id="element-154">
              <UndoIcon className="h-4 w-4 text-gray-500" data-id="element-155" />
            </button>
            <button onClick={redo} disabled={historyIdx >= history.length - 1} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" title="Redo" data-id="element-156">
              <RedoIcon className="h-4 w-4 text-gray-500" data-id="element-157" />
            </button>
          </div>

          <div className="flex items-center gap-2" data-id="element-158">
            {showBeautifyInput && <div className="flex items-center gap-1.5 bg-white border border-purple-200 rounded-lg px-2 py-1 shadow-sm" data-id="element-159">
                <input type="text" value={beautifyPrompt} onChange={e => setBeautifyPrompt(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') handleBeautify(beautifyPrompt);
              if (e.key === 'Escape') {
                setShowBeautifyInput(false);
                setBeautifyPrompt('');
              }
            }} placeholder="e.g. warm colors, minimal, corporate..." className="text-xs border-none outline-none bg-transparent w-48 placeholder:text-gray-400" autoFocus data-id="element-160" />
                <button onClick={() => handleBeautify(beautifyPrompt)} disabled={isBeautifying} className="p-1 text-purple-600 hover:text-purple-800 disabled:opacity-50" data-id="element-161">
                  <SendIcon className="h-3.5 w-3.5" data-id="element-162" />
                </button>
                <button onClick={() => {
              setShowBeautifyInput(false);
              setBeautifyPrompt('');
            }} className="p-1 text-gray-400 hover:text-gray-600" data-id="element-163">
                  <XIcon className="h-3.5 w-3.5" data-id="element-164" />
                </button>
              </div>}
            <button onClick={() => {
            if (showBeautifyInput) handleBeautify(beautifyPrompt);else if (blocks.length > 0) setShowBeautifyInput(true);
          }} disabled={isBeautifying || blocks.length === 0} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 disabled:opacity-50 transition-colors" title="Use AI to beautify your email" data-id="element-165">
              {isBeautifying ? <Loader2Icon className="h-3.5 w-3.5 animate-spin" data-id="element-166" /> : <WandSparklesIcon className="h-3.5 w-3.5" data-id="element-167" />}
              {isBeautifying ? 'Beautifying...' : '✨ AI Beautify'}
            </button>
            <span className="text-xs text-gray-400" data-id="element-168">
              {blocks.length} block{blocks.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className="mx-auto rounded-lg border border-gray-200 shadow-sm overflow-hidden" style={{
        maxWidth: styles.contentWidth,
        backgroundColor: styles.backgroundColor
      }} data-id="element-169">
          <div style={{
          backgroundColor: '#ffffff'
        }} data-id="element-170">
            {blocks.length === 0 && <div onDragOver={e => {
            e.preventDefault();
            setDragOverIdx(0);
          }} onDragLeave={() => setDragOverIdx(null)} onDrop={handleCanvasDropEnd} className={`border-2 border-dashed rounded-lg p-16 text-center transition-colors m-4 ${dragOverIdx === 0 ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}`} data-id="element-171">
                <p className="text-sm text-gray-400" data-id="element-172">Drag email blocks here</p>
              </div>}

            {blocks.map((block, idx) => <Fragment key={block.id} data-id="element-173">
                <div onDragOver={handleDragOver(idx)} onDragLeave={() => {
              setDragOverIdx(null);
              setDragOverCol(null);
            }} onDrop={handleDrop(idx)} className={`transition-all ${dragOverIdx === idx ? 'h-2 bg-blue-400' : 'h-0.5'}`} data-id="element-174" />
                <div draggable onDragStart={handleCanvasDragStart(idx)} onDragOver={handleBlockDragOver(idx)} onDrop={handleBlockDrop(idx)} onClick={e => {
              e.stopPropagation();
              setSelectedId(block.id);
              setSelectedChild(null);
            }} className={`group relative border-2 transition-colors ${selectedId === block.id && !selectedChild ? 'border-blue-500' : 'border-transparent hover:border-gray-300'}`} data-id="element-175">
                  <div className="absolute right-1 top-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10" data-id="element-176">
                    <button onClick={e => {
                  e.stopPropagation();
                  duplicateBlock(block.id);
                }} className="p-1 bg-white border border-gray-200 rounded shadow-sm hover:bg-gray-50" data-id="element-177">
                      <CopyIcon className="h-3 w-3 text-gray-500" data-id="element-178" />
                    </button>
                    <button onClick={e => {
                  e.stopPropagation();
                  deleteBlock(block.id);
                }} className="p-1 bg-white border border-gray-200 rounded shadow-sm hover:bg-red-50" data-id="element-179">
                      <Trash2Icon className="h-3 w-3 text-red-500" data-id="element-180" />
                    </button>
                  </div>
                  <div className="absolute left-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-181">
                    <GripVerticalIcon className="h-4 w-4 text-gray-400 cursor-grab" data-id="element-182" />
                  </div>

                  {/* Special rendering for columns with drop zones */}
                  {block.type === 'columns' ? <EmailColumnsCanvas block={block} styles={styles} selectedChild={selectedChild} dragOverCol={dragOverCol} onColumnDragOver={handleColumnDragOver} onColumnDrop={handleColumnDrop} onSelectChild={(colIndex, childId) => {
                setSelectedChild({
                  parentId: block.id,
                  colIndex,
                  childId
                });
                setSelectedId(null);
              }} onDeleteChild={(colIndex, childId) => deleteChildBlock(block.id, colIndex, childId)} data-id="element-183" /> : <EmailCanvasBlock block={block} styles={styles} data-id="element-184" />}
                </div>
              </Fragment>)}

            {blocks.length > 0 && <div onDragOver={e => {
            e.preventDefault();
            setDragOverIdx(blocks.length);
          }} onDragLeave={() => setDragOverIdx(null)} onDrop={handleDrop(blocks.length)} className={`transition-all ${dragOverIdx === blocks.length ? 'h-2 bg-blue-400' : 'h-4'}`} data-id="element-185" />}
          </div>
        </div>
      </div>

      {/* Right — Properties */}
      <div className="w-72 flex-shrink-0 border-l border-gray-200 bg-white overflow-y-auto" data-id="element-186">
        {selectedBlock ? <EmailBlockProperties block={selectedBlock} isChild={!!selectedChild} onChange={handlePropertyChange} onBlur={commitToHistory} onDelete={handlePropertyDelete} data-id="element-187" /> : <EmailStyleProperties styles={styles} onChange={setStyles} data-id="element-188" />}
      </div>
    </div>;
}
// ── Columns Canvas with Drop Zones ──
function EmailColumnsCanvas({
  block,
  styles,
  selectedChild,
  dragOverCol,
  onColumnDragOver,
  onColumnDrop,
  onSelectChild,
  onDeleteChild
}: {
  block: EmailBlock;
  styles: EmailGlobalStyles;
  selectedChild: {
    parentId: string;
    colIndex: number;
    childId: string;
  } | null;
  dragOverCol: {
    parentId: string;
    colIndex: number;
    position: number;
  } | null;
  onColumnDragOver: (parentId: string, colIndex: number) => (e: React.DragEvent) => void;
  onColumnDrop: (parentId: string, colIndex: number) => (e: React.DragEvent) => void;
  onSelectChild: (colIndex: number, childId: string) => void;
  onDeleteChild: (colIndex: number, childId: string) => void;
}) {
  const pad = {
    paddingTop: block.paddingTop ?? 10,
    paddingBottom: block.paddingBottom ?? 10,
    paddingLeft: block.paddingLeft ?? 20,
    paddingRight: block.paddingRight ?? 20
  };
  const bg = block.backgroundColor || undefined;
  const cols = block.columns || [[], []];
  const isOver = (colIdx: number) => dragOverCol?.parentId === block.id && dragOverCol?.colIndex === colIdx;
  return <div style={{
    ...pad,
    backgroundColor: bg
  }} data-id="element-189">
      <div style={{
      display: 'flex',
      gap: 0
    }} data-id="element-190">
        {cols.map((col, colIdx) => <div key={colIdx} onDragOver={onColumnDragOver(block.id, colIdx)} onDragLeave={e => e.stopPropagation()} onDrop={onColumnDrop(block.id, colIdx)} onClick={e => e.stopPropagation()} style={{
        flex: 1,
        border: isOver(colIdx) ? '2px dashed #3b82f6' : '1px dashed #d1d5db',
        backgroundColor: isOver(colIdx) ? '#eff6ff' : undefined,
        minHeight: 60,
        padding: 4,
        transition: 'border-color 0.15s, background-color 0.15s'
      }} data-id="element-191">
            {col.length === 0 ? <p style={{
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: 12,
          padding: '16px 8px'
        }} data-id="element-192">
                {isOver(colIdx) ? '⬇ Drop here' : `Column ${colIdx + 1} — drag blocks here`}
              </p> : col.map(cb => <div key={cb.id} onClick={e => {
          e.stopPropagation();
          onSelectChild(colIdx, cb.id);
        }} className="relative group/child" style={{
          border: selectedChild?.parentId === block.id && selectedChild?.childId === cb.id ? '2px solid #3b82f6' : '1px solid transparent',
          borderRadius: 4,
          marginBottom: 2,
          cursor: 'pointer'
        }} data-id="element-193">
                  <div className="absolute -right-0.5 -top-0.5 opacity-0 group-hover/child:opacity-100 transition-opacity z-10" data-id="element-194">
                    <button onClick={e => {
              e.stopPropagation();
              onDeleteChild(colIdx, cb.id);
            }} className="p-0.5 bg-white border border-gray-200 rounded shadow-sm hover:bg-red-50" title="Remove from column" data-id="element-195">
                      <XIcon className="h-2.5 w-2.5 text-red-500" data-id="element-196" />
                    </button>
                  </div>
                  <EmailCanvasBlock block={cb} styles={styles} data-id="element-197" />
                </div>)}
          </div>)}
      </div>
    </div>;
}
// ── Canvas Preview ──
function EmailCanvasBlock({
  block,
  styles
}: {
  block: EmailBlock;
  styles: EmailGlobalStyles;
}) {
  const pad = {
    paddingTop: block.paddingTop ?? 10,
    paddingBottom: block.paddingBottom ?? 10,
    paddingLeft: block.paddingLeft ?? 20,
    paddingRight: block.paddingRight ?? 20
  };
  const bg = block.backgroundColor || undefined;
  switch (block.type) {
    case 'text':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        fontFamily: styles.fontFamily,
        fontSize: block.fontSize || 16,
        lineHeight: block.lineHeight || 1.6,
        color: block.color || styles.textColor,
        textAlign: block.align || 'left'
      }} data-id="element-198">
          {block.content || 'Add text...'}
        </div>;
    case 'heading':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        fontFamily: styles.fontFamily,
        fontSize: block.fontSize || 28,
        fontWeight: block.fontWeight || 'bold',
        lineHeight: block.lineHeight || 1.3,
        color: block.color || '#111827',
        textAlign: block.align || 'center'
      }} data-id="element-199">
          {block.content || 'Heading'}
        </div>;
    case 'button':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        textAlign: block.align || 'center'
      }} data-id="element-200">
          <span style={{
          display: block.buttonWidth === 'full' ? 'block' : 'inline-block',
          padding: '12px 28px',
          backgroundColor: block.buttonBgColor || styles.linkColor,
          color: block.buttonTextColor || '#fff',
          borderRadius: block.buttonBorderRadius ?? 6,
          fontFamily: styles.fontFamily,
          fontSize: 16,
          fontWeight: 600,
          textAlign: 'center'
        }} data-id="element-201">
            {block.buttonText || 'Click Here'}
          </span>
        </div>;
    case 'image':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        textAlign: block.align || 'center'
      }} data-id="element-202">
          {block.src ? <img src={block.src} alt={block.alt} style={{
          maxWidth: `${block.imageWidth || 100}%`,
          height: 'auto',
          margin: block.align === 'center' ? '0 auto' : undefined,
          display: 'block'
        }} data-id="element-203" /> : <div style={{
          background: '#f3f4f6',
          border: '2px dashed #d1d5db',
          padding: 40,
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: 14
        }} data-id="element-204">
              Image — set URL in properties
            </div>}
        </div>;
    case 'video_thumbnail':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        textAlign: block.align || 'center'
      }} data-id="element-205">
          <div style={{
          position: 'relative',
          display: 'inline-block'
        }} data-id="element-206">
            {block.src ? <img src={block.src} alt={block.alt} style={{
            maxWidth: '100%',
            borderRadius: 8
          }} data-id="element-207" /> : <div style={{
            background: '#1f2937',
            padding: '60px 80px',
            borderRadius: 8,
            color: '#fff',
            fontSize: 14
          }} data-id="element-208">
                Video Thumbnail
              </div>}
            <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%,-50%)',
            width: 50,
            height: 50,
            background: 'rgba(0,0,0,0.6)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} data-id="element-209">
              <PlayIcon className="h-5 w-5 text-white ml-0.5" data-id="element-210" />
            </div>
          </div>
        </div>;
    case 'divider':
      return <div style={{
        ...pad,
        backgroundColor: bg
      }} data-id="element-211">
          <hr style={{
          border: 'none',
          borderTop: `${block.dividerThickness || 1}px ${block.dividerStyle || 'solid'} ${block.dividerColor || '#e5e7eb'}`
        }} data-id="element-212" />
        </div>;
    case 'spacer':
      return <div style={{
        height: block.height || 20,
        backgroundColor: bg
      }} className="flex items-center justify-center text-xs text-gray-400" data-id="element-213">
          ↕ {block.height || 20}px
        </div>;
    case 'social':
      return <div style={{
        ...pad,
        backgroundColor: bg,
        textAlign: block.align || 'center'
      }} data-id="element-214">
          <div style={{
          display: 'inline-flex',
          gap: 8
        }} data-id="element-215">
            {(block.socialLinks || []).map((link, i) => <div key={i} style={{
            width: block.socialIconSize || 32,
            height: block.socialIconSize || 32,
            backgroundColor: '#6b7280',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 10,
            fontWeight: 'bold'
          }} data-id="element-216">
                {link.platform.charAt(0).toUpperCase()}
              </div>)}
          </div>
        </div>;
    default:
      return <div style={pad} data-id="element-217">Unknown block</div>;
  }
}
// ── Properties Panels ──
function EmailBlockProperties({
  block,
  isChild,
  onChange,
  onBlur,
  onDelete
}: {
  block: EmailBlock;
  isChild: boolean;
  onChange: (u: Partial<EmailBlock>) => void;
  onBlur: () => void;
  onDelete: () => void;
}) {
  return <div className="p-4 space-y-4" data-id="element-218">
      <div className="flex items-center justify-between pb-3 border-b border-gray-200" data-id="element-219">
        <div data-id="element-220">
          <h3 className="text-sm font-semibold text-gray-900 capitalize" data-id="element-221">
            {block.type.replace('_', ' ')}
          </h3>
          {isChild && <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded font-medium" data-id="element-222">
              Inside column
            </span>}
        </div>
        <button onClick={onDelete} className="p-1 text-red-500 hover:bg-red-50 rounded" data-id="element-223">
          <Trash2Icon className="h-4 w-4" data-id="element-224" />
        </button>
      </div>

      {(block.type === 'text' || block.type === 'heading') && <>
          <EProp label="Content" data-id="element-225">
            <textarea value={block.content || ''} onChange={e => onChange({
          content: e.target.value
        })} onBlur={onBlur} rows={4} className="prop-input text-sm" data-id="element-226" />
          </EProp>
          <EProp label="Font Size" data-id="element-227">
            <input type="number" value={block.fontSize || 16} onChange={e => onChange({
          fontSize: Number(e.target.value)
        })} onBlur={onBlur} min={10} max={72} className="prop-input" data-id="element-228" />
          </EProp>
          <EProp label="Color" data-id="element-229">
            <div className="flex gap-2" data-id="element-230">
              <input type="color" value={block.color || '#333333'} onChange={e => onChange({
            color: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-231" />
              <input type="text" value={block.color || '#333333'} onChange={e => onChange({
            color: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-232" />
            </div>
          </EProp>
          <EProp label="Align" data-id="element-233">
            <select value={block.align || 'left'} onChange={e => {
          onChange({
            align: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-234">
              <option value="left" data-id="element-235">Left</option>
              <option value="center" data-id="element-236">Center</option>
              <option value="right" data-id="element-237">Right</option>
            </select>
          </EProp>
          {block.type === 'heading' && <EProp label="Font Weight" data-id="element-238">
              <select value={block.fontWeight || 'bold'} onChange={e => {
          onChange({
            fontWeight: e.target.value
          });
          onBlur();
        }} className="prop-input" data-id="element-239">
                <option value="normal" data-id="element-240">Normal</option>
                <option value="500" data-id="element-241">Medium</option>
                <option value="600" data-id="element-242">Semibold</option>
                <option value="bold" data-id="element-243">Bold</option>
              </select>
            </EProp>}
          <EProp label="Line Height" data-id="element-244">
            <input type="number" value={block.lineHeight || 1.6} onChange={e => onChange({
          lineHeight: Number(e.target.value)
        })} onBlur={onBlur} min={1} max={3} step={0.1} className="prop-input" data-id="element-245" />
          </EProp>
        </>}

      {block.type === 'button' && <>
          <EProp label="Button Text" data-id="element-246">
            <input type="text" value={block.buttonText || ''} onChange={e => onChange({
          buttonText: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-247" />
          </EProp>
          <EProp label="URL" data-id="element-248">
            <input type="url" value={block.buttonUrl || ''} onChange={e => onChange({
          buttonUrl: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="https://..." data-id="element-249" />
          </EProp>
          <EProp label="Background" data-id="element-250">
            <div className="flex gap-2" data-id="element-251">
              <input type="color" value={block.buttonBgColor || '#2563eb'} onChange={e => onChange({
            buttonBgColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-252" />
              <input type="text" value={block.buttonBgColor || '#2563eb'} onChange={e => onChange({
            buttonBgColor: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-253" />
            </div>
          </EProp>
          <EProp label="Text Color" data-id="element-254">
            <div className="flex gap-2" data-id="element-255">
              <input type="color" value={block.buttonTextColor || '#ffffff'} onChange={e => onChange({
            buttonTextColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-256" />
              <input type="text" value={block.buttonTextColor || '#ffffff'} onChange={e => onChange({
            buttonTextColor: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-257" />
            </div>
          </EProp>
          <EProp label="Border Radius" data-id="element-258">
            <input type="number" value={block.buttonBorderRadius ?? 6} onChange={e => onChange({
          buttonBorderRadius: Number(e.target.value)
        })} onBlur={onBlur} min={0} max={50} className="prop-input" data-id="element-259" />
          </EProp>
          <EProp label="Width" data-id="element-260">
            <select value={block.buttonWidth || 'auto'} onChange={e => {
          onChange({
            buttonWidth: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-261">
              <option value="auto" data-id="element-262">Auto</option>
              <option value="full" data-id="element-263">Full Width</option>
            </select>
          </EProp>
          <EProp label="Align" data-id="element-264">
            <select value={block.align || 'center'} onChange={e => {
          onChange({
            align: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-265">
              <option value="left" data-id="element-266">Left</option>
              <option value="center" data-id="element-267">Center</option>
              <option value="right" data-id="element-268">Right</option>
            </select>
          </EProp>
        </>}

      {block.type === 'image' && <>
          <EProp label="Image URL" data-id="element-269">
            <input type="url" value={block.src || ''} onChange={e => onChange({
          src: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="https://..." data-id="element-270" />
          </EProp>
          <EProp label="Alt Text" data-id="element-271">
            <input type="text" value={block.alt || ''} onChange={e => onChange({
          alt: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-272" />
          </EProp>
          <EProp label="Width %" data-id="element-273">
            <input type="number" value={block.imageWidth || 100} onChange={e => onChange({
          imageWidth: Number(e.target.value)
        })} onBlur={onBlur} min={10} max={100} className="prop-input" data-id="element-274" />
          </EProp>
          <EProp label="Link URL" data-id="element-275">
            <input type="url" value={block.href || ''} onChange={e => onChange({
          href: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="Optional link" data-id="element-276" />
          </EProp>
        </>}

      {block.type === 'video_thumbnail' && <>
          <EProp label="Video URL" data-id="element-277">
            <input type="url" value={block.videoUrl || ''} onChange={e => onChange({
          videoUrl: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="YouTube or Vimeo URL" data-id="element-278" />
          </EProp>
          <EProp label="Thumbnail URL" data-id="element-279">
            <input type="url" value={block.src || ''} onChange={e => onChange({
          src: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="Auto-detected from YouTube" data-id="element-280" />
          </EProp>
          <EProp label="Alt Text" data-id="element-281">
            <input type="text" value={block.alt || ''} onChange={e => onChange({
          alt: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-282" />
          </EProp>
        </>}

      {block.type === 'divider' && <>
          <EProp label="Color" data-id="element-283">
            <div className="flex gap-2" data-id="element-284">
              <input type="color" value={block.dividerColor || '#e5e7eb'} onChange={e => onChange({
            dividerColor: e.target.value
          })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-285" />
              <input type="text" value={block.dividerColor || '#e5e7eb'} onChange={e => onChange({
            dividerColor: e.target.value
          })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" data-id="element-286" />
            </div>
          </EProp>
          <EProp label="Thickness" data-id="element-287">
            <input type="number" value={block.dividerThickness || 1} onChange={e => onChange({
          dividerThickness: Number(e.target.value)
        })} onBlur={onBlur} min={1} max={10} className="prop-input" data-id="element-288" />
          </EProp>
          <EProp label="Style" data-id="element-289">
            <select value={block.dividerStyle || 'solid'} onChange={e => {
          onChange({
            dividerStyle: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-290">
              <option value="solid" data-id="element-291">Solid</option>
              <option value="dashed" data-id="element-292">Dashed</option>
              <option value="dotted" data-id="element-293">Dotted</option>
            </select>
          </EProp>
        </>}

      {block.type === 'spacer' && <EProp label="Height (px)" data-id="element-294">
          <input type="number" value={block.height || 20} onChange={e => onChange({
        height: Number(e.target.value)
      })} onBlur={onBlur} min={4} max={200} className="prop-input" data-id="element-295" />
        </EProp>}

      {block.type === 'columns' && <EProp label="Column Ratio" data-id="element-296">
          <select value={block.columnRatio || '50-50'} onChange={e => {
        const ratio = e.target.value as any;
        const colCount = ratio === '33-33-33' ? 3 : 2;
        const cols = [...(block.columns || [[], []])];
        while (cols.length < colCount) cols.push([]);
        onChange({
          columnRatio: ratio,
          columns: cols.slice(0, colCount)
        });
        onBlur();
      }} className="prop-input" data-id="element-297">
            <option value="50-50" data-id="element-298">50 / 50</option>
            <option value="33-67" data-id="element-299">33 / 67</option>
            <option value="67-33" data-id="element-300">67 / 33</option>
            <option value="33-33-33" data-id="element-301">33 / 33 / 33</option>
          </select>
        </EProp>}

      {/* Social Links Editor */}
      {block.type === 'social' && <div className="space-y-3" data-id="element-302">
          <div className="flex items-center justify-between" data-id="element-303">
            <span className="text-xs font-medium text-gray-700" data-id="element-304">
              Social Links
            </span>
            <button onClick={() => {
          const links = [...(block.socialLinks || [])];
          // Find a platform not yet used
          const used = new Set(links.map(l => l.platform));
          const next = SOCIAL_PLATFORMS.find(p => !used.has(p)) || 'facebook';
          links.push({
            platform: next,
            url: '#'
          });
          onChange({
            socialLinks: links
          });
          onBlur();
        }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5" data-id="element-305">
              <PlusIcon className="h-3 w-3" data-id="element-306" /> Add
            </button>
          </div>
          {(block.socialLinks || []).map((link, i) => <div key={i} className="space-y-1 p-2 bg-gray-50 rounded-md border border-gray-100" data-id="element-307">
              <div className="flex items-center gap-1" data-id="element-308">
                <select value={link.platform} onChange={e => {
            const links = [...(block.socialLinks || [])];
            links[i] = {
              ...links[i],
              platform: e.target.value
            };
            onChange({
              socialLinks: links
            });
          }} className="prop-input flex-1 text-xs capitalize" data-id="element-309">
                  {SOCIAL_PLATFORMS.map(p => <option key={p} value={p} data-id="element-310">
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </option>)}
                </select>
                <button onClick={() => {
            const links = (block.socialLinks || []).filter((_, j) => j !== i);
            onChange({
              socialLinks: links
            });
            onBlur();
          }} className="p-1 text-gray-400 hover:text-red-500" data-id="element-311">
                  <XIcon className="h-3 w-3" data-id="element-312" />
                </button>
              </div>
              <input type="url" value={link.url} onChange={e => {
          const links = [...(block.socialLinks || [])];
          links[i] = {
            ...links[i],
            url: e.target.value
          };
          onChange({
            socialLinks: links
          });
        }} onBlur={onBlur} placeholder="https://..." className="prop-input text-xs w-full" data-id="element-313" />
            </div>)}
          <EProp label="Icon Size" data-id="element-314">
            <input type="number" value={block.socialIconSize || 32} onChange={e => onChange({
          socialIconSize: Number(e.target.value)
        })} onBlur={onBlur} min={16} max={64} className="prop-input" data-id="element-315" />
          </EProp>
          <EProp label="Align" data-id="element-316">
            <select value={block.align || 'center'} onChange={e => {
          onChange({
            align: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-317">
              <option value="left" data-id="element-318">Left</option>
              <option value="center" data-id="element-319">Center</option>
              <option value="right" data-id="element-320">Right</option>
            </select>
          </EProp>
        </div>}

      {/* Padding — shared for all blocks */}
      <div className="pt-3 border-t border-gray-200" data-id="element-321">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-322">
          Padding
        </span>
        <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-323">
          <EProp label="Top" data-id="element-324">
            <input type="number" value={block.paddingTop ?? 10} onChange={e => onChange({
            paddingTop: Number(e.target.value)
          })} onBlur={onBlur} min={0} className="prop-input" data-id="element-325" />
          </EProp>
          <EProp label="Bottom" data-id="element-326">
            <input type="number" value={block.paddingBottom ?? 10} onChange={e => onChange({
            paddingBottom: Number(e.target.value)
          })} onBlur={onBlur} min={0} className="prop-input" data-id="element-327" />
          </EProp>
          <EProp label="Left" data-id="element-328">
            <input type="number" value={block.paddingLeft ?? 20} onChange={e => onChange({
            paddingLeft: Number(e.target.value)
          })} onBlur={onBlur} min={0} className="prop-input" data-id="element-329" />
          </EProp>
          <EProp label="Right" data-id="element-330">
            <input type="number" value={block.paddingRight ?? 20} onChange={e => onChange({
            paddingRight: Number(e.target.value)
          })} onBlur={onBlur} min={0} className="prop-input" data-id="element-331" />
          </EProp>
        </div>
      </div>

      <EProp label="Background" data-id="element-332">
        <div className="flex gap-2" data-id="element-333">
          <input type="color" value={block.backgroundColor || '#ffffff'} onChange={e => onChange({
          backgroundColor: e.target.value
        })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-334" />
          <input type="text" value={block.backgroundColor || ''} onChange={e => onChange({
          backgroundColor: e.target.value
        })} onBlur={onBlur} className="prop-input flex-1 font-mono text-xs" placeholder="transparent" data-id="element-335" />
        </div>
      </EProp>
    </div>;
}
function EmailStyleProperties({
  styles,
  onChange
}: {
  styles: EmailGlobalStyles;
  onChange: (s: EmailGlobalStyles) => void;
}) {
  return <div className="p-4 space-y-4" data-id="element-336">
      <div className="pb-3 border-b border-gray-200" data-id="element-337">
        <div className="flex items-center gap-2" data-id="element-338">
          <PaletteIcon className="h-4 w-4 text-gray-400" data-id="element-339" />
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-340">Email Styles</h3>
        </div>
        <p className="text-xs text-gray-500 mt-1" data-id="element-341">
          Select a block to edit its properties
        </p>
      </div>
      <EProp label="Background Color" data-id="element-342">
        <div className="flex gap-2" data-id="element-343">
          <input type="color" value={styles.backgroundColor} onChange={e => onChange({
          ...styles,
          backgroundColor: e.target.value
        })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-344" />
          <input type="text" value={styles.backgroundColor} onChange={e => onChange({
          ...styles,
          backgroundColor: e.target.value
        })} className="prop-input flex-1 font-mono text-xs" data-id="element-345" />
        </div>
      </EProp>
      <EProp label="Content Width" data-id="element-346">
        <input type="number" value={styles.contentWidth} onChange={e => onChange({
        ...styles,
        contentWidth: Number(e.target.value)
      })} min={400} max={800} className="prop-input" data-id="element-347" />
      </EProp>
      <EProp label="Font Family" data-id="element-348">
        <select value={styles.fontFamily} onChange={e => onChange({
        ...styles,
        fontFamily: e.target.value
      })} className="prop-input" data-id="element-349">
          <option value="Arial, Helvetica, sans-serif" data-id="element-350">Arial</option>
          <option value="Georgia, serif" data-id="element-351">Georgia</option>
          <option value="'Trebuchet MS', sans-serif" data-id="element-352">Trebuchet MS</option>
          <option value="Verdana, sans-serif" data-id="element-353">Verdana</option>
          <option value="'Times New Roman', serif" data-id="element-354">Times New Roman</option>
        </select>
      </EProp>
      <EProp label="Text Color" data-id="element-355">
        <div className="flex gap-2" data-id="element-356">
          <input type="color" value={styles.textColor} onChange={e => onChange({
          ...styles,
          textColor: e.target.value
        })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-357" />
          <input type="text" value={styles.textColor} onChange={e => onChange({
          ...styles,
          textColor: e.target.value
        })} className="prop-input flex-1 font-mono text-xs" data-id="element-358" />
        </div>
      </EProp>
      <EProp label="Link Color" data-id="element-359">
        <div className="flex gap-2" data-id="element-360">
          <input type="color" value={styles.linkColor} onChange={e => onChange({
          ...styles,
          linkColor: e.target.value
        })} className="h-8 w-8 rounded border cursor-pointer" data-id="element-361" />
          <input type="text" value={styles.linkColor} onChange={e => onChange({
          ...styles,
          linkColor: e.target.value
        })} className="prop-input flex-1 font-mono text-xs" data-id="element-362" />
        </div>
      </EProp>
    </div>;
}
function EProp({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-363">
      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-364">
        {label}
      </label>
      {children}
    </div>;
}